import { ChatComposer, ChatComposerDrawer } from "@astryxdesign/core/Chat";
import { Token } from "@astryxdesign/core/Token";
import { useMemo, useState } from "react";
import type { Category } from "../../shared/types";
import {
  domainOf,
  extractUrls,
  faviconUrl,
  parseComposerText,
  type ParsedInput,
} from "../lib/format";

interface Props {
  activeCategory: Category | "all";
  onAdd: (parsed: ParsedInput, category: Category) => void;
}

/**
 * Capture box: a chat-style composer that grows into multiline as you type
 * (Enter submits, Shift+Enter for a new line). URLs found anywhere in the
 * text surface live as clickable chips; on submit they become link previews,
 * the first plain-text line becomes the title and the rest becomes notes.
 */
export function QuickAdd({ activeCategory, onAdd }: Props) {
  const [value, setValue] = useState("");
  const urls = useMemo(() => extractUrls(value), [value]);

  function submit(raw: string) {
    const parsed = parseComposerText(raw);
    if (!parsed) return;
    onAdd(parsed, activeCategory === "all" ? "do" : activeCategory);
    setValue("");
  }

  const removeUrl = (url: string) =>
    setValue((v) => v.replaceAll(url, "").replaceAll(url.replace(/^https:\/\//, ""), "").trim());

  return (
    <ChatComposer
      value={value}
      onChange={setValue}
      onSubmit={submit}
      placeholder="Add an idea, or paste a link…"
      {...(urls.length > 0
        ? {
            drawer: (
              <ChatComposerDrawer>
                {urls.map((url) => {
                  const domain = domainOf(url);
                  return (
                    <Token
                      key={url}
                      label={domain}
                      href={url}
                      icon={
                        <img
                          src={faviconUrl(domain)}
                          alt=""
                          width={12}
                          height={12}
                          style={{ borderRadius: 2, display: "block" }}
                        />
                      }
                      onRemove={() => removeUrl(url)}
                    />
                  );
                })}
              </ChatComposerDrawer>
            ),
          }
        : {})}
    />
  );
}
